import { ArrowRight, TrainFront } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { apiPaths, type DeparturesResponse, type StopsResponse } from '../../../shared/api/contract.ts';
import { DEFAULT_CITY } from '../../../shared/cities/index.ts';
import { minutesUntil, upcomingDepartures, type TransportDeparture } from '../../../shared/domain/index.ts';
import { FRESHNESS_POLICIES } from '../../../shared/freshness/index.ts';
import { LineBadge } from '../../components/LineBadge.tsx';
import { SectionCard } from '../../components/SectionCard.tsx';
import { SkeletonRows } from '../../components/Skeleton.tsx';
import { useApi } from '../../hooks/useApi.ts';
import { formatTime } from '../../lib/format.ts';
import { readPreference, writePreference } from '../../lib/storage.ts';

const isString = (v: unknown): v is string => typeof v === 'string';

/** « Voie 2 », « Retard · prévu 19:54 · voie 1 », « Horaire théorique ». */
function departureMeta(departure: TransportDeparture): string {
  const platform = departure.platform === undefined ? null : `voie ${departure.platform}`;
  if (departure.status === 'cancelled') return 'Supprimé';
  if (departure.status === 'delayed' && departure.scheduledAt !== undefined) {
    return ['Retard', `prévu ${formatTime(departure.scheduledAt)}`, platform].filter(isString).join(' · ');
  }
  if (!departure.isRealtime) return ['Horaire théorique', platform].filter(isString).join(' · ');
  return departure.platform === undefined ? 'Temps réel' : `Voie ${departure.platform}`;
}

function DepartureRow({ departure, now }: { departure: TransportDeparture; now: Date }) {
  const minutes = minutesUntil(departure, now);
  const cancelled = departure.status === 'cancelled';
  return (
    <li className="departure">
      <LineBadge line={departure.line} />
      <div className="departure__main">
        <span className="departure__destination">{departure.destination}</span>
        <span className={`departure__meta departure__meta--${departure.status}`}>{departureMeta(departure)}</span>
      </div>
      <div className="departure__time">
        {cancelled ? (
          <span className="departure__cancelled">Supprimé</span>
        ) : minutes === 0 ? (
          <span>à quai</span>
        ) : (
          <span>
            {minutes}
            <span aria-hidden="true"> min</span>
            <span className="visually-hidden"> minutes</span>
          </span>
        )}
        <time dateTime={departure.expectedAt}>{formatTime(departure.expectedAt)}</time>
      </div>
    </li>
  );
}

interface DeparturesBoardProps {
  readonly limit?: number;
  readonly initialStopId?: string | null;
  readonly headingLevel?: 2 | 3;
  readonly showMoreLink?: boolean;
  readonly className?: string;
}

export function DeparturesBoard({
  limit = 5,
  initialStopId = null,
  headingLevel = 2,
  showMoreLink = false,
  className,
}: DeparturesBoardProps) {
  const city = DEFAULT_CITY.slug;
  const stops = useApi<StopsResponse>(apiPaths.stops(city));
  const [chosen, setChosen] = useState<string | null>(
    () => initialStopId ?? readPreference('favorite-stop', null, isString),
  );
  const stopList = stops.data?.stops ?? [];
  const stopId = stopList.some((s) => s.id === chosen) ? chosen : (stops.data?.defaultStopId ?? null);
  const departures = useApi<DeparturesResponse>(
    stopId === null ? null : apiPaths.departures(city, stopId),
    FRESHNESS_POLICIES.departures.refreshMs,
  );

  const select = (id: string) => {
    setChosen(id);
    writePreference('favorite-stop', id);
  };

  const tabs =
    stopList.length > 1 ? (
      <div className="tabs" role="group" aria-label="Choisir une gare">
        {stopList.map((s) => (
          <button key={s.id} type="button" className="tab" aria-pressed={s.id === stopId} onClick={() => select(s.id)}>
            {s.name}
          </button>
        ))}
      </div>
    ) : undefined;

  return (
    <SectionCard
      title="Prochains départs"
      icon={<TrainFront size={22} aria-hidden="true" />}
      kind="departures"
      subject="transport"
      section={departures.data?.departures}
      loading={departures.loading || stops.loading}
      fetchError={departures.error ?? stops.error}
      headingLevel={headingLevel}
      toolbar={tabs}
      skeleton={<SkeletonRows rows={4} />}
      className={className}
    >
      {(list, now) => {
        const upcoming = upcomingDepartures(list, now).slice(0, limit);
        return (
          <>
            {upcoming.length === 0 ? (
              <p className="state-message">Aucun départ annoncé dans les prochaines minutes.</p>
            ) : (
              <ul className="departures" aria-live="off">
                {upcoming.map((d) => (
                  <DepartureRow key={d.id} departure={d} now={now} />
                ))}
              </ul>
            )}
            {showMoreLink && (
              <Link className="text-link departures__more" to={`/transports?gare=${stopId ?? ''}`}>
                Voir tous les départs <ArrowRight size={16} aria-hidden="true" />
              </Link>
            )}
          </>
        );
      }}
    </SectionCard>
  );
}
